import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {Map<string, {url: string, alt: string, fp: string}>} */
  #variantMediaMap = new Map();

  /** @type {number} */
  #swapGeneration = 0;

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#parseVariantMediaMap();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Parses the variant media map JSON from the embedded script tag.
   */
  #parseVariantMediaMap() {
    this.#variantMediaMap.clear();
    const script = this.querySelector('script[data-variant-media-map]');
    if (!script?.textContent) return;

    try {
      const data = JSON.parse(script.textContent);

      for (const [id, info] of Object.entries(data)) {
        this.#variantMediaMap.set(id, /** @type {any} */ (info));
      }
    } catch {
      // No variant media data available
    }
  }

  /**
   * Handles the VariantSelectedEvent (fires instantly on swatch click, BEFORE fetch).
   * Creates a crossfade overlay with the new variant's featured image.
   * @param {Event & {detail: {resource: {id: string}}}} event
   */
  #handleVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;
    if (!optionValueId) return;

    const mediaData = this.#variantMediaMap.get(optionValueId);
    if (!mediaData) return;

    this.#showInstantPreview(mediaData);
  };

  /**
   * Shows an instant preview overlay with the variant's featured image.
   * Overlay is positioned inside slideshow-container so it inherits gallery styles.
   * @param {{url: string, alt: string, fp: string}} mediaData
   */
  #showInstantPreview(mediaData) {
    ++this.#swapGeneration;

    const container = this.querySelector('slideshow-container');
    if (!container) return;

    // Build srcset
    const widths = [240, 352, 832, 1200, 1600, 1920];
    const srcset = widths.map((w) => `${mediaData.url.replace(/(width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
    const src = mediaData.url.replace(/(width=)\d+/, '$11920');

    // Get or create overlay
    let overlay = this.querySelector('.media-gallery__crossfade-overlay');

    if (!(overlay instanceof HTMLImageElement)) {
      overlay = document.createElement('img');
      overlay.className = 'media-gallery__crossfade-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      container.appendChild(overlay);
    }

    // Reset to invisible without transition
    overlay.style.transition = 'none';
    overlay.style.opacity = '0';
    overlay.style.objectPosition = mediaData.fp || 'center';

    overlay.srcset = srcset;
    overlay.src = src;
    overlay.alt = mediaData.alt || '';

    // Force reflow then fade in
    void overlay.offsetHeight;
    overlay.style.transition = '';
    overlay.style.opacity = '1';
  }

  /**
   * Creates a snapshot overlay from the currently visible slide.
   * Used when no instant preview was triggered (e.g., dropdown change).
   */
  #createSnapshotOverlay() {
    const container = this.querySelector('slideshow-container');
    if (!container) return;

    const currentImage = container.querySelector('.swiper-slide-active img, .swiper-slide:first-child img');
    if (!(currentImage instanceof HTMLImageElement)) return;

    let overlay = this.querySelector('.media-gallery__crossfade-overlay');

    if (!(overlay instanceof HTMLImageElement)) {
      overlay = document.createElement('img');
      overlay.className = 'media-gallery__crossfade-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      container.appendChild(overlay);
    }

    overlay.src = currentImage.currentSrc || currentImage.src;
    overlay.srcset = currentImage.srcset || '';
    overlay.alt = '';
    overlay.style.objectPosition = currentImage.style.objectPosition || 'center';
    overlay.style.transition = 'none';
    overlay.style.opacity = '1';

    void overlay.offsetHeight;
  }

  /**
   * Fades out and removes the crossfade overlay.
   * @param {number} generation - The swap generation to guard against stale transitions.
   */
  #fadeOutOverlay(generation) {
    const overlay = this.querySelector('.media-gallery__crossfade-overlay');
    if (!overlay) return;

    overlay.style.transition = '';
    overlay.style.opacity = '0';

    const cleanup = () => {
      if (this.#swapGeneration !== generation) return;
      overlay.remove();
    };

    overlay.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, 500);
  }

  /**
   * Handles a variant update event.
   * Uses morph to update the gallery in-place, preserving navigation and structure.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const generation = ++this.#swapGeneration;

    // If no instant preview overlay exists, create a snapshot from current state
    if (!this.querySelector('.media-gallery__crossfade-overlay')) {
      this.#createSnapshotOverlay();
    }

    // Detach the overlay before morph so it isn't removed during DOM diff
    const overlay = this.querySelector('.media-gallery__crossfade-overlay');
    if (overlay) overlay.remove();

    // Remove Swiper's loop duplicate slides before morphing
    for (const dup of this.querySelectorAll('.swiper-slide-duplicate')) {
      dup.remove();
    }

    // Clean up Swiper-specific inline styles on slides and wrapper
    for (const slide of this.querySelectorAll('.swiper-slide')) {
      slide.removeAttribute('style');
      slide.classList.remove(
        'swiper-slide-active',
        'swiper-slide-next',
        'swiper-slide-prev',
        'swiper-slide-visible'
      );
    }

    const swiperWrapper = this.querySelector('.swiper-wrapper');
    if (swiperWrapper) swiperWrapper.removeAttribute('style');

    // Morph gallery in-place — preserves navigation, thumbnails, controls
    morph(this, newMediaGallery, MORPH_OPTIONS);

    // Re-attach overlay inside the new slideshow container
    const newContainer = this.querySelector('slideshow-container');
    if (overlay && newContainer) {
      newContainer.appendChild(overlay);
    }

    // Refresh refs to pick up any changed children
    this.updatedCallback();

    // Reinitialize the slideshow's Swiper to recognize new slide DOM
    this.refs.slideshow?.reinitialize();

    // Re-parse the variant media map
    this.#parseVariantMediaMap();

    // Re-attach zoom dialog listener (ref may have changed)
    const { signal } = this.#controller;
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Fade out overlay after new content has painted
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#fadeOutOverlay(generation);
      });
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
